import { securePost, secureGet, securePut, secureDelete } from "../../Config/ApiConfig";
import { removeNullUndefinedWithReduce } from "../../Utils/javascript";
import { deleteConversation } from "../Messages/MessagesServices";

const virtualChannelRequestByAgentId = new Map();
const VIRTUAL_CHANNELS_PAGE_SIZE = 100;
const MAX_VIRTUAL_CHANNEL_LOOKUP_PAGES = 20;
const DEFAULT_VIRTUAL_CHANNEL_TYPE = 'facebook_page';

const getAgentTestChannelType = () => DEFAULT_VIRTUAL_CHANNEL_TYPE;

const resolveVirtualChannel = async (agent) => {
    const existingChannels = await getVirtualChannels(agent.id);
    if (existingChannels.length > 0) {
        return existingChannels[0];
    }

    return createVirtualChannel(
        agent.id,
        getAgentTestChannelType(agent),
        `${agent.name} - Test Channel`
    );
};

/**
 * Fetch virtual channels for an agent.
 * @param {string} agentId - The agent ID
 * @returns {Promise<Array>} Array of virtual channels
 */
export const getVirtualChannels = async (agentId) => {
    try {
        const filters = removeNullUndefinedWithReduce({
            agent_id: agentId,
            is_virtual: true
        });
        const encodedFilters = encodeURIComponent(JSON.stringify(filters));

        for (let page = 1; page <= MAX_VIRTUAL_CHANNEL_LOOKUP_PAGES; page += 1) {
            const { data } = await secureGet(
                `/channels?page=${page}&page_size=${VIRTUAL_CHANNELS_PAGE_SIZE}&sort_by=created_at&sort_direction=desc&filters=${encodedFilters}`
            );

            const channels = Array.isArray(data?.data) ? data.data : [];
            if (channels.length > 0) {
                return channels;
            }

            if (!data?.metadata?.has_next) {
                break;
            }
        }

        return [];
    } catch (error) {
        throw new Error(`Failed to fetch virtual channels: ${error.message}`);
    }
};

/**
 * Create a virtual channel for an agent.
 * @param {string} agentId - The agent ID
 * @param {string} channelType - The channel type (e.g., 'facebook_page')
 * @param {string} name - Optional name for the channel
 * @returns {Promise<Object>} The created virtual channel
 */
export const createVirtualChannel = async (agentId, channelType, name = null, description = null) => {
    try {
        const channelData = removeNullUndefinedWithReduce({
            channel_type: channelType,
            name: name,
            description: description,
        });

        const { data } = await securePost(`/agents/${agentId}/virtual-channels`, channelData);
        return data?.data;
    } catch (error) {
        throw new Error(`Failed to create virtual channel: ${error.message}`);
    }
};

/**
 * Update a virtual channel.
 * @param {string} channelId - The virtual channel ID
 * @param {Object} values - Editable virtual channel data
 * @returns {Promise<Object>} The updated virtual channel
 */
export const updateVirtualChannel = async (channelId, values = {}) => {
    try {
        const channelData = removeNullUndefinedWithReduce({
            name: values.name,
            description: values.description,
        });

        const { data } = await securePut(`/channels/${channelId}`, channelData);
        return data?.data;
    } catch (error) {
        throw new Error(`Failed to update virtual channel: ${error.message}`);
    }
};

/**
 * Delete a virtual channel.
 * @param {string} channelId - The virtual channel ID
 * @returns {Promise<Object>} The response data
 */
export const deleteVirtualChannel = async (channelId) => {
    try {
        const { data } = await secureDelete(`/channels/${channelId}`);
        return data;
    } catch (error) {
        throw new Error(`Failed to delete virtual channel: ${error.message}`);
    }
};

/**
 * Get or create a virtual channel for an agent.
 * Uses the agent's first linked channel type, or defaults to 'facebook_page'.
 * @param {Object} agent - The agent object with id, name, and channels
 * @returns {Promise<Object>} The virtual channel
 */
export const getOrCreateVirtualChannel = async (agent) => {
    if (!agent?.id) {
        throw new Error('Failed to get or create virtual channel: Agent id is required');
    }

    if (virtualChannelRequestByAgentId.has(agent.id)) {
        return virtualChannelRequestByAgentId.get(agent.id);
    }

    const request = resolveVirtualChannel(agent)
        .catch((error) => {
            throw new Error(`Failed to get or create virtual channel: ${error.message}`);
        })
        .finally(() => {
            virtualChannelRequestByAgentId.delete(agent.id);
        });

    virtualChannelRequestByAgentId.set(agent.id, request);
    return request;
};

/**
 * Fetch virtual contacts for an agent (via its virtual channel).
 * @param {string} agentId - The agent ID
 * @returns {Promise<Array>} Array of virtual contacts
 */
export const getVirtualContacts = async (agentId, knownVirtualChannelId = null) => {
    try {
        let virtualChannelId = knownVirtualChannelId;

        if (!virtualChannelId) {
            // First get the virtual channel for this agent
            const virtualChannels = await getVirtualChannels(agentId);
            if (virtualChannels.length === 0) {
                return [];
            }
            virtualChannelId = virtualChannels[0].id;
        }

        const filters = removeNullUndefinedWithReduce({ channel_id: virtualChannelId });
        const { data } = await secureGet(
            `/contacts?page=1&page_size=10&sort_by=created_at&sort_direction=desc&filters=${encodeURIComponent(JSON.stringify(filters))}`
        );

        if (data.data && data.data.length > 0) {
            return data.data;
        }
        return [];
    } catch (error) {
        throw new Error(`Failed to fetch virtual contacts: ${error.message}`);
    }
};

/**
 * Create a virtual contact under a virtual channel.
 * @param {string} channelId - The virtual channel ID
 * @param {Object} virtualContactData - Contact data overrides
 * @returns {Promise<Object>} The created virtual contact
 */
export const createVirtualContact = async (channelId, virtualContactData = {}) => {
    try {
        const defaultContactData = {
            first_name: "Test",
            last_name: "Customer",
            email: "test.customer@example.com",
            phone: "1234567890",
            locale: "en_US",
            timezone: -new Date().getTimezoneOffset() / 60,
            gender: "male",
            note: "Virtual test customer created for agent testing",
            ...virtualContactData
        };

        const { data } = await securePost(`/channels/${channelId}/virtual-contacts`, defaultContactData);
        return data?.data;
    } catch (error) {
        throw new Error(`Failed to create virtual contact: ${error.message}`);
    }
};

/**
 * Get conversation for a virtual contact.
 * @param {string} contactId - The contact ID
 * @returns {Promise<Object>} The conversation
 */
export const getVirtualContactConversation = async (contactId, signal) => {
    try {
        const { data } = signal
            ? await secureGet(`/contacts/${contactId}/conversation`, { signal })
            : await secureGet(`/contacts/${contactId}/conversation`);
        return data?.data;
    } catch (error) {
        throw new Error(`Failed to get virtual contact conversation: ${error.message}`);
    }
};

/**
 * Reset the conversation for a virtual contact.
 * Deletes the current conversation when present, then fetches the contact conversation again.
 * The backend creates a fresh conversation for the virtual contact when none exists.
 * @param {string} contactId - The virtual contact ID
 * @param {string} conversationId - The current conversation ID
 * @returns {Promise<Object>} The fresh conversation
 */
export const resetVirtualContactConversation = async (contactId, conversationId) => {
    try {
        if (conversationId) {
            await deleteConversation(conversationId);
        }
        return getVirtualContactConversation(contactId);
    } catch (error) {
        throw new Error(`Failed to reset virtual contact conversation: ${error.message}`);
    }
};

/**
 * Get existing virtual conversation for an agent.
 * @param {string} agentId - The agent ID
 * @returns {Promise<Object|null>} The virtual conversation or null
 */
export const getExistingVirtualConversation = async (agentId) => {
    try {
        // Get virtual channel first
        const virtualChannels = await getVirtualChannels(agentId);
        if (virtualChannels.length === 0) {
            return null;
        }

        const virtualChannelId = virtualChannels[0].id;
        const filters = removeNullUndefinedWithReduce({ channel_id: virtualChannelId });
        const { data } = await secureGet(
            `/conversations?page=1&page_size=5&sort_by=updated_time&sort_direction=desc&filters=${encodeURIComponent(JSON.stringify(filters))}`
        );

        if (data.data && data.data.length > 0) {
            return data.data[0];
        }
        return null;
    } catch (error) {
        throw new Error(`Failed to fetch virtual conversations: ${error.message}`);
    }
};

/**
 * Start an agent testing conversation.
 * Creates virtual channel and contact if needed.
 * @param {Object} agent - The agent object
 * @returns {Promise<Object>} The conversation
 */
export const startAgentTestingConversation = async (agent) => {
    try {
        // Step 1: Ensure virtual channel exists
        const virtualChannel = await getOrCreateVirtualChannel(agent);

        // Step 2: Check if virtual contacts exist for this channel
        let virtualContacts = await getVirtualContacts(agent.id, virtualChannel.id);

        // Step 3: If no virtual contacts exist, create a default one
        if (virtualContacts.length === 0) {
            const newVirtualContact = await createVirtualContact(virtualChannel.id);
            virtualContacts = [newVirtualContact];
        }

        // Step 4: Use the first virtual contact to get/create conversation
        const virtualContact = virtualContacts[0];
        const conversation = await getVirtualContactConversation(virtualContact.id);

        return conversation;
    } catch (error) {
        throw new Error(`Failed to start test conversation: ${error.message}`);
    }
};

/**
 * Delete the agent testing conversation.
 * @param {string} agentId - The agent ID
 * @returns {Promise<boolean>} True if deleted, false if not found
 */
export const deleteAgentTestingConversation = async (agentId) => {
    try {
        const existing = await getExistingVirtualConversation(agentId);
        if (existing?.id) {
            await deleteConversation(existing.id);
            return true;
        }
        return false;
    } catch (error) {
        throw new Error(`Failed to delete test conversation: ${error.message}`);
    }
};

/**
 * Send a message as a virtual contact to the agent.
 * @param {string} virtualContactId - The virtual contact ID
 * @param {Object} messageData - The message data
 * @returns {Promise<Object>} The sent message
 */
export const sendAgentTestingMessage = async (virtualContactId, messageData) => {
    try {
        const { data } = await securePost(
            `/contacts/${virtualContactId}/messages`,
            messageData
        );
        return data;
    } catch (error) {
        throw new Error(`Failed to send message to virtual contact: ${error.message}`);
    }
};

/**
 * Delete a virtual contact.
 * @param {string} contactId - The contact ID
 * @returns {Promise<Object>} The response data
 */
export const deleteVirtualContact = async (contactId) => {
    try {
        const { data } = await secureDelete(`/contacts/${contactId}`);
        return data;
    } catch (error) {
        throw new Error(`Failed to delete virtual contact: ${error.message}`);
    }
};
